import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { 
  CheckCircle, 
  Star, 
  Crown, 
  Sparkles, 
  Bot, 
  MessageCircle, 
  Coins, 
  Zap,
  ExternalLink,
  Smartphone
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useSubscription, PRODUCTS } from '@/contexts/SubscriptionContext';

// Main subscription plans
const PLANS = [
  {
    name: 'Explore',
    price: 'Free',
    priceId: null,
    productId: null,
    description: 'Start your wellness journey',
    features: [
      '14-day trial of all features',
      'Daily check-ins',
      'Basic tracking tools',
      'Oracle cards',
      'Community access',
    ],
    icon: Sparkles,
    isFree: true,
  },
  {
    name: 'Foundations',
    price: '£11.99',
    priceId: 'price_1SEEGHDADiSUiGodWuALEof9',
    productId: 'prod_TAZPXkiLL1pD8u',
    description: '14-day free trial then £11.99/month',
    features: [
      'All Explore features',
      'Full tracking suite',
      'All courses & meditations',
      'Advanced manifestation tools',
      'Vision board creator',
      'Hope reel creator',
      'Crisis support suite',
    ],
    icon: Star,
    popular: true,
  },
  {
    name: 'Mastery',
    price: '£19.99',
    priceId: 'price_1SEEGYDADiSUiGodBhd8Gtgs',
    productId: 'prod_TAZPpPxlUlSDGn',
    description: '14-day trial then £19.99/month',
    features: [
      'Everything in Foundations',
      'Advanced analytics',
      'Priority support',
      'Exclusive content',
      'Early access to new tools',
      'Personalised insights',
      'Premium guidance',
    ],
    icon: Crown,
    popular: false,
  },
];

// Therappi AI tiers
const THERAPPI_TIERS = [
  {
    id: 'therappi-free',
    name: 'Free Therappi',
    price: 'Free',
    priceId: null,
    productId: null,
    description: 'Try AI Therappi® with limited credits',
    features: [
      '20 non-rolling credits per month',
      '~6 Therappi® conversations',
      'Basic AI chatbot (text-only)',
      'Crisis resources'
    ],
    icon: MessageCircle,
    tier: 'free',
  },
  {
    id: 'therappi-standard',
    name: 'Standard Therappi',
    price: '£12.99',
    priceId: 'price_1SEEH4DADiSUiGod8KwavKx5',
    productId: 'prod_TAZPNU6FJXvArq',
    description: 'Full AI Therappi® access',
    features: [
      '300 monthly credits',
      'Advanced AI chatbot',
      'Text conversations',
      'End-to-end encrypted',
      'Crisis resources'
    ],
    icon: Bot,
    badge: 'Most Popular',
  },
  {
    id: 'therappi-premium',
    name: 'Premium Therappi',
    price: '£24.99',
    priceId: 'price_NEW_PREMIUM_THERAPPI',
    productId: 'prod_NEW_PREMIUM_THERAPPI',
    description: 'Personalised AI with your data',
    features: [
      '300 monthly credits',
      'Personalised AI',
      'Daily tracker integration',
      'Life patterns analysis',
      'Priority support'
    ],
    icon: Crown,
    highlight: true,
  }
];

// Credit packs
const CREDIT_PACKS = [
  {
    name: '100 Credits',
    credits: 100,
    price: '£4.99',
    priceId: 'price_credit_100',
    description: 'Perfect for occasional use',
  },
  {
    name: '250 Credits',
    credits: 250,
    price: '£9.99',
    priceId: 'price_credit_250',
    description: 'Great value for regular users',
    savings: '£2.50 saved',
    popular: true,
  },
  {
    name: '500 Credits',
    credits: 500,
    price: '£17.99',
    priceId: 'price_credit_500',
    description: 'Best value for heavy users',
    savings: '£7 saved',
  },
  {
    name: '1000 Credits',
    credits: 1000,
    price: '£29.99',
    priceId: 'price_credit_1000',
    description: 'Maximum value',
    savings: '£20 saved',
    badge: 'Best Deal',
  },
];

export default function Subscribe() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [activeTab, setActiveTab] = useState('subscriptions');
  const navigate = useNavigate();
  
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      setIsLoading(false);
    });
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubscribe = async (priceId: string) => {
    if (!user) {
      toast.info('Please sign in to subscribe');
      navigate('/auth?redirect=/subscribe');
      return;
    }
    
    if (!termsAccepted || !privacyAccepted) {
      toast.error('Please accept the Terms & Conditions and Privacy Policy');
      return;
    }
    
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { price_id: priceId }
      });
      
      if (error) throw error;
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to create checkout session');
    }
  };

  const handlePurchaseCredits = async (priceId: string) => {
    if (!user) {
      toast.info('Please sign in to purchase credits');
      navigate('/auth?redirect=/subscribe');
      return;
    }
    
    if (!termsAccepted || !privacyAccepted) {
      toast.error('Please accept the Terms & Conditions and Privacy Policy');
      return;
    }
    
    try {
      const { data, error } = await supabase.functions.invoke('purchase-credits', {
        body: { price_id: priceId }
      });
      
      if (error) throw error;
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error purchasing credits:', error);
      toast.error('Failed to create credit purchase session');
    }
  };

  const legalAccepted = termsAccepted && privacyAccepted;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <span className="font-semibold text-lg">Therappi</span>
          </div>
          
          {user ? (
            <Button variant="outline" size="sm" onClick={() => navigate('/')}>
              Back to App
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => navigate('/auth?redirect=/subscribe')}>
              Sign In
            </Button>
          )}
        </div>
      </header>

      <div className="container mx-auto py-8 px-4">
        {/* Mobile App Banner */}
        {isNative && (
          <Card className="mb-6 border-primary/20 bg-primary/5">
            <CardContent className="p-4 flex items-center gap-3">
              <Smartphone className="h-5 w-5 text-primary flex-shrink-0" />
              <p className="text-sm text-muted-foreground">
                You're using the Therappi app. Purchases are processed securely through our website.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Choose Your Plan</h1>
          <p className="text-muted-foreground">
            Unlock your full wellness potential with Therappi
          </p>
        </div>

        {/* Legal Acceptance */}
        <Card className="max-w-2xl mx-auto mb-8">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-semibold">Before you subscribe</h2>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <Checkbox 
                  id="terms" 
                  checked={termsAccepted}
                  onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                />
                <Label htmlFor="terms" className="text-sm leading-relaxed cursor-pointer">
                  I have read and agree to the{' '}
                  <button
                    onClick={() => window.open('/terms', '_blank')}
                    className="text-primary underline hover:text-primary/80"
                  >
                    Terms & Conditions
                  </button>
                </Label>
              </div>
              <div className="flex items-start space-x-3">
                <Checkbox 
                  id="privacy" 
                  checked={privacyAccepted}
                  onCheckedChange={(checked) => setPrivacyAccepted(checked as boolean)}
                />
                <Label htmlFor="privacy" className="text-sm leading-relaxed cursor-pointer">
                  I have read and understand the{' '}
                  <button
                    onClick={() => window.open('/privacy', '_blank')}
                    className="text-primary underline hover:text-primary/80"
                  >
                    Privacy Policy
                  </button>
                </Label>
              </div>
            </div>
            {!legalAccepted && (
              <p className="text-sm text-muted-foreground">
                Please accept both to proceed with purchases.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
            <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
            <TabsTrigger value="credits">Credit Packs</TabsTrigger>
          </TabsList>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions" className="space-y-12">
            {/* Main Plans */}
            <div>
              <h2 className="text-xl font-bold text-center mb-6">Wellness Plans</h2>
              <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {PLANS.map((plan) => {
                  const Icon = plan.icon;
                  return (
                    <Card 
                      key={plan.name} 
                      className={`relative ${plan.popular ? 'border-primary shadow-lg md:scale-105' : ''}`}
                    >
                      {plan.popular && (
                        <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          Most Popular
                        </Badge>
                      )}
                      
                      <CardHeader className="text-center pb-4">
                        <div className="mx-auto mb-4 p-3 rounded-full bg-primary/10 w-fit">
                          <Icon className="h-8 w-8 text-primary" />
                        </div>
                        <CardTitle className="text-xl">{plan.name}</CardTitle>
                        <CardDescription>{plan.description}</CardDescription>
                        <div className="mt-4">
                          <span className="text-3xl font-bold">{plan.price}</span>
                          {!plan.isFree && <span className="text-muted-foreground">/month</span>}
                        </div>
                      </CardHeader>
                      
                      <CardContent className="space-y-4">
                        <ul className="space-y-2">
                          {plan.features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                              <span className="text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                        
                        <Button 
                          onClick={() => plan.priceId && handleSubscribe(plan.priceId)}
                          className="w-full"
                          disabled={plan.isFree || !legalAccepted}
                          variant={plan.isFree ? "outline" : "default"}
                        >
                          {plan.isFree ? (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Always Free
                            </>
                          ) : (
                            <>
                              Start Free Trial
                              <ExternalLink className="h-4 w-4 ml-2" />
                            </>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Therappi AI Tiers */}
            <div>
              <h2 className="text-xl font-bold text-center mb-2">Therappi® AI Add-On</h2>
              <p className="text-muted-foreground text-center mb-6">
                Advanced AI therapy powered by Claude Sonnet 4.5
              </p>
              <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {THERAPPI_TIERS.map((tier) => {
                  const Icon = tier.icon;
                  return (
                    <Card 
                      key={tier.id} 
                      className={`relative ${tier.highlight ? 'border-primary shadow-lg' : ''}`}
                    >
                      {tier.badge && (
                        <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary">
                          {tier.badge}
                        </Badge>
                      )}
                      {tier.highlight && !tier.badge && (
                        <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-primary to-secondary">
                          Best Value
                        </Badge>
                      )}
                      
                      <CardHeader className="text-center pb-4">
                        <div className="mx-auto mb-4 p-3 rounded-full bg-primary/10 w-fit">
                          <Icon className="h-8 w-8 text-primary" />
                        </div>
                        <CardTitle className="text-lg">{tier.name}</CardTitle>
                        <CardDescription>{tier.description}</CardDescription>
                        <div className="mt-4">
                          <span className="text-2xl font-bold">{tier.price}</span>
                          {tier.price !== 'Free' && <span className="text-muted-foreground">/month</span>}
                        </div>
                      </CardHeader>
                      
                      <CardContent className="space-y-4">
                        <ul className="space-y-2">
                          {tier.features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                              <span className="text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                        
                        <Button
                          onClick={() => tier.priceId && handleSubscribe(tier.priceId)}
                          className="w-full"
                          disabled={!tier.priceId || !legalAccepted}
                          variant={tier.highlight ? "default" : "outline"}
                        >
                          {tier.priceId ? (
                            <>
                              Subscribe
                              <ExternalLink className="h-4 w-4 ml-2" />
                            </>
                          ) : 'Free Plan'}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* Credits Tab */}
          <TabsContent value="credits">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-xl font-bold mb-2">Buy Credit Packs</h2>
                <p className="text-muted-foreground">
                  Credits never expire and can be used for AI Therappi® conversations
                </p>
              </div>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {CREDIT_PACKS.map((pack) => (
                  <Card 
                    key={pack.name} 
                    className={`relative ${pack.popular ? 'border-primary shadow-lg' : ''}`}
                  >
                    {pack.badge && (
                      <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-amber-500 to-orange-500">
                        {pack.badge}
                      </Badge>
                    )}
                    {pack.popular && !pack.badge && (
                      <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary">
                        Popular
                      </Badge>
                    )}
                    
                    <CardHeader className="text-center pb-2">
                      <div className="mx-auto mb-2 p-2 rounded-full bg-amber-500/10 w-fit">
                        <Coins className="h-6 w-6 text-amber-500" />
                      </div>
                      <CardTitle className="text-lg">{pack.name}</CardTitle>
                      <CardDescription>{pack.description}</CardDescription>
                    </CardHeader>
                    
                    <CardContent className="text-center space-y-3">
                      <div>
                        <span className="text-2xl font-bold">{pack.price}</span>
                        {pack.savings && (
                          <p className="text-xs text-green-600 font-medium mt-1">{pack.savings}</p>
                        )}
                      </div>
                      
                      <Button
                        onClick={() => handlePurchaseCredits(pack.priceId)}
                        className="w-full"
                        disabled={!legalAccepted}
                        variant={pack.popular || pack.badge ? "default" : "outline"}
                      >
                        Buy Now
                        <ExternalLink className="h-4 w-4 ml-2" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              <p className="text-center text-sm text-muted-foreground mt-6">
                1 credit = 1 AI Therappi® message exchange. Credits never expire.
              </p>
            </div>
          </TabsContent>
        </Tabs>

        {/* FAQ / Support */}
        <div className="text-center mt-12 text-sm text-muted-foreground">
          <p>
            Questions? Email us at{' '}
            <a href="mailto:support@therappi.com" className="text-primary underline">
              support@therappi.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
